import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";

function Yearpavati() {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef();

  const udiseNumber = localStorage.getItem("udiseNumber");
  const language = localStorage.getItem("language");
  const { schoolDetails, feeDetails, dateFrom, dateTo, selectedClass, division, year: selectedYear } = location.state || {};

  const [studentData, setStudentData] = useState({});
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);

  /* ---------------- DATE PARSING & FORMATTING ---------------- */
  const parseDateToNormalizedString = (dateInput) => {
    if (!dateInput) return null;
    if (typeof dateInput === 'string') {
      const trimmed = dateInput.trim();
      if (!trimmed || trimmed === 'N/A' || trimmed === '0') return null;

      // Match DD-MM-YYYY or DD/MM/YYYY (e.g. 29-09-2026, 29/09/2026)
      const dmyMatch = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
      if (dmyMatch) {
        const day = dmyMatch[1].padStart(2, '0');
        const month = dmyMatch[2].padStart(2, '0');
        const year = dmyMatch[3];
        return `${year}-${month}-${day}`;
      }

      // Match YYYY-MM-DD or YYYY/MM/DD (e.g. 2026-09-29, 2026/09/29)
      const ymdMatch = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
      if (ymdMatch) {
        const year = ymdMatch[1];
        const month = ymdMatch[2].padStart(2, '0');
        const day = ymdMatch[3].padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      // Fallback: try Date constructor
      const d = new Date(trimmed);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } else if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
      const year = dateInput.getFullYear();
      const month = String(dateInput.getMonth() + 1).padStart(2, '0');
      const day = String(dateInput.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } else if (typeof dateInput === 'number') {
      const d = new Date(dateInput);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const normalized = parseDateToNormalizedString(dateString);
    if (!normalized) return dateString;
    const [year, month, day] = normalized.split('-');
    return `${day}-${month}-${year}`;
  };

  /* ---------------- FETCH STUDENTS ---------------- */
  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_FIREBASE_DATABASE_URL}/schoolRegister/${udiseNumber}/studentData.json`
        );
        const data = await res.json();
        setStudentData(data || {});
        setIsDataFetched(true);
      } catch (err) {
        console.error(err);
      }
    };
    fetchStudentData();
  }, [udiseNumber]);

  /* ---------------- FIXED FEE LOGIC ---------------- */
  const organizeFeeDetails = () => {
    if (!feeDetails || feeDetails.length === 0) {
      return { invoiceGroups: {} };
    }

    const normalizedFrom = parseDateToNormalizedString(dateFrom);
    const normalizedTo = parseDateToNormalizedString(dateTo);

    // Step 1: Consolidate fee records by invoice (per student and invoiceNo)
    const invoicesMap = {};

    feeDetails.forEach((fee) => {
      const studentKey = fee.serialNo;
      const invNo = fee.invoiceNo;
      if (!studentKey || !invNo || invNo === 'N/A' || invNo === '-') return;

      const key = `${studentKey}_${invNo}`;
      const beforePayVal = parseFloat(fee.beforePay || 0);
      const payAmtVal = parseFloat(fee.payAmount || fee.paidFee || 0);
      const student = studentData[studentKey] || studentData[parseInt(studentKey, 10)] || {};
      const feeClass = fee.class || fee.currentClass || student.currentClass || '';
      const feeDiv = fee.division || student.division || '';
      const feeDate = fee.date || fee.pavatiDate || '';

      if (!invoicesMap[key]) {
        invoicesMap[key] = {
          serialNo: studentKey,
          invoiceNo: invNo,
          date: feeDate,
          class: feeClass,
          division: feeDiv,
          year: fee.year || fee.educationalYear || '',
          transaction: fee.transaction || fee.transactionType || 'By Cash',
          beforePay: beforePayVal,
          payAmountSum: payAmtVal,
        };
      } else {
        if (beforePayVal > invoicesMap[key].beforePay) {
          invoicesMap[key].beforePay = beforePayVal;
        }
        invoicesMap[key].payAmountSum += payAmtVal;
        if (!invoicesMap[key].class && feeClass) invoicesMap[key].class = feeClass;
        if (!invoicesMap[key].division && feeDiv) invoicesMap[key].division = feeDiv;
        if (!invoicesMap[key].transaction && fee.transaction) invoicesMap[key].transaction = fee.transaction;
        if (!invoicesMap[key].date && feeDate) invoicesMap[key].date = feeDate;
      }
    });

    // Step 2: Sort invoice entries by date and invoiceNo for each student to calculate paidAmount
    const invoiceList = Object.values(invoicesMap).sort((a, b) => {
      const dateA = parseDateToNormalizedString(a.date) || '';
      const dateB = parseDateToNormalizedString(b.date) || '';
      const dateCompare = dateA.localeCompare(dateB);
      return dateCompare !== 0 ? dateCompare : (parseInt(a.invoiceNo, 10) || 0) - (parseInt(b.invoiceNo, 10) || 0);
    });

    const studentLastPay = {};
    const invoiceGroups = {};

    invoiceList.forEach((inv) => {
      const studentKey = inv.serialNo;
      if (!studentLastPay[studentKey]) {
        studentLastPay[studentKey] = 0;
      }

      let paidAmount = 0;
      if (inv.payAmountSum > 0) {
        paidAmount = inv.payAmountSum;
      } else {
        paidAmount = Math.max(0, inv.beforePay - studentLastPay[studentKey]);
      }

      if (inv.beforePay > studentLastPay[studentKey]) {
        studentLastPay[studentKey] = inv.beforePay;
      }

      const normalizedFeeDate = parseDateToNormalizedString(inv.date);
      if (!normalizedFeeDate) return;

      const feeClass = inv.class;
      const feeDiv = inv.division;
      const feeYear = inv.year;

      const isClassMatch = !selectedClass || selectedClass === "N/A" || selectedClass === "All" || selectedClass === "All Standards" || selectedClass === "" ||
        (feeClass && feeClass.toString().trim() === selectedClass.toString().trim());

      const isDivisionMatch = !division || division === "N/A" || division === "All" || division === " " || division === "" ||
        (feeDiv && feeDiv.toString().trim() === division.toString().trim());

      const isYearMatch = !selectedYear || selectedYear === "N/A" || selectedYear === "All" || selectedYear === "All Years" || selectedYear === "" ||
        (feeYear && feeYear.toString().trim() === selectedYear.toString().trim());

      const isInRange = Boolean(
        (!normalizedFrom || normalizedFeeDate >= normalizedFrom) &&
        (!normalizedTo || normalizedFeeDate <= normalizedTo) &&
        isClassMatch &&
        isDivisionMatch &&
        isYearMatch
      );

      if (isInRange && paidAmount > 0) {
        const groupDateKey = normalizedFeeDate;
        if (!invoiceGroups[groupDateKey]) {
          invoiceGroups[groupDateKey] = {};
        }

        invoiceGroups[groupDateKey][inv.invoiceNo] = {
          class: feeClass,
          division: feeDiv,
          serialNo: inv.serialNo,
          invoiceNo: inv.invoiceNo,
          pavatiDate: inv.date,
          transactionType: inv.transaction,
          beforePay: inv.beforePay,
          paidAmount: paidAmount,
        };
      }
    });

    return { invoiceGroups };
  };

  const { invoiceGroups } = isDataFetched
    ? organizeFeeDetails()
    : { invoiceGroups: {} };

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const originalContents = document.body.innerHTML;

    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload(); // Reload to restore original content after print
  };

  const handleGoBack = () => {
    navigate(-1); // Use navigate to go back
  };

  const handleSendWhatsAppToHMAndClerk = async () => {
    if (isSendingWhatsApp) return;
    setIsSendingWhatsApp(true);
    try {
      if (!udiseNumber) {
        alert("UDISE Number not found. Please log in again.");
        setIsSendingWhatsApp(false);
        return;
      }

      // Fetch WhatsApp configuration from Firebase
      const res = await fetch(
        `${process.env.REACT_APP_FIREBASE_DATABASE_URL}/schoolRegister/${udiseNumber}/whatsappConfig.json`
      );
      const whatsappConfig = await res.json();

      const rawNumbers = `${whatsappConfig?.feesStaffNumbers || ''} ${whatsappConfig?.attendanceStaffNumbers || ''}`.trim();
      const staffNumbers = [...new Set(
        rawNumbers
          .split(/[\s,;\n]+/)
          .map((n) => n.replace(/\D/g, ""))
          .filter(Boolean)
          .map((n) => (n.length === 10 ? `91${n}` : n))
      )];

      if (staffNumbers.length === 0) {
        alert("Headmaster / Clerk WhatsApp numbers are not configured in WhatsApp Management settings. Please configure them in WhatsApp Management.");
        setIsSendingWhatsApp(false);
        return;
      }

      const webhookUrlTemplate = whatsappConfig?.feesWebhook || whatsappConfig?.attendanceWebhook;
      if (!webhookUrlTemplate) {
        alert("WhatsApp Webhook URL is not configured in WhatsApp Management. Please set the Webhook URL in WhatsApp Management.");
        setIsSendingWhatsApp(false);
        return;
      }

      const schoolNameValue = schoolDetails?.schoolName || localStorage.getItem('schoolName') || 'Digital School';
      const fromStr = dateFrom ? formatDate(dateFrom) : '';
      const toStr = dateTo ? formatDate(dateTo) : '';
      const dateRangeStr = fromStr && toStr ? `${fromStr} to ${toStr}` : (fromStr || toStr || 'All Dates');
      const formattedDate = new Date().toLocaleDateString('en-GB');

      let totalPaidFeeSum = 0;
      let memberDetailsList = [];
      const sortedDates = Object.keys(invoiceGroups).sort();

      sortedDates.forEach((dateKey) => {
        const activeInvoices = Object.values(invoiceGroups[dateKey]).filter((group) => {
          const student = studentData[group.serialNo] || {};
          const paidAmt = parseFloat(group.paidAmount || 0);
          return paidAmt > 0 && student.isActive !== false;
        });

        activeInvoices.forEach((group, idx) => {
          const student = studentData[group.serialNo] || {};
          const stdFullName = student.stdName
            ? `${student.stdName} ${student.stdFather || ''} ${student.stdSurname || ''}`.trim()
            : (group.serialNo || '-');
          const paidAmt = parseFloat(group.paidAmount || 0);
          totalPaidFeeSum += paidAmt;
          const clsName = group.class || student.currentClass || '';
          const divName = group.division || student.division || '';
          const clsDivStr = clsName ? `(${clsName}${divName ? '-' + divName : ''})` : '';

          memberDetailsList.push(`${idx + 1}. ${stdFullName} ${clsDivStr} - Inv: ${group.invoiceNo || '-'} - ₹${paidAmt.toFixed(2)}`);
        });
      });

      const memberDetailsText = memberDetailsList.join('\n');
      const grandTotalStr = `₹${totalPaidFeeSum.toFixed(2)}`;
      const selectedClassStr = selectedClass && selectedClass !== 'N/A' && selectedClass !== 'All' ? selectedClass : 'All Classes';
      const selectedDivStr = division && division !== 'N/A' && division !== 'All' ? division : 'All';

      // Formatted text message (matching Attendance message format)
      const fullReportText = 
`📚 Fee Collection Report Update

Dear Headmaster / Clerk,

This is to inform you that the Fee Collection Report has been generated.

📅 Date / Period: ${dateRangeStr}
🏫 School: ${schoolNameValue}
📚 Class: ${selectedClassStr} - ${selectedDivStr}
💰 Total Paid Fee: ${grandTotalStr}

📝 Report Details:
${memberDetailsText || 'No entries found'}

Regards,
${schoolNameValue}`;

      // Send background Webhook requests for each staff phone number
      const sendPromises = staffNumbers.map(async (phone) => {
        let url = webhookUrlTemplate;

        // 1. Replace number
        if (url.includes('number=')) {
          url = url.replace(/number=[^&]+/, `number=${phone}`);
        } else {
          url += (url.includes('?') ? '&' : '?') + `number=${phone}`;
        }

        // 2. Replace Attendance style placeholders if present
        url = url
          .replace(/studentname/gi, encodeURIComponent(`Fee Report (${dateRangeStr})`))
          .replace(/attendancestatus/gi, encodeURIComponent(`Grand Total: ${grandTotalStr}`))
          .replace(/date/gi, encodeURIComponent(dateRangeStr || formattedDate))
          .replace(/class/gi, encodeURIComponent(selectedClassStr))
          .replace(/division/gi, encodeURIComponent(selectedDivStr))
          .replace(/schoolname/gi, encodeURIComponent(schoolNameValue))
          .replace(/remark/gi, encodeURIComponent(`Total: ${grandTotalStr} | Members: ${memberDetailsList.length}`))
          .replace(/contactnumber/gi, encodeURIComponent(phone));

        // 3. Replace Fees style placeholders if present
        url = url
          .replace(/StudentName/g, encodeURIComponent(`Fee Report (${dateRangeStr})`))
          .replace(/DueDate/g, encodeURIComponent(dateRangeStr || formattedDate))
          .replace(/TotalFee/g, encodeURIComponent(grandTotalStr))
          .replace(/PaidAmount/g, encodeURIComponent(grandTotalStr))
          .replace(/PendingAmount/g, encodeURIComponent('0.00'))
          .replace(/PaymentMode/g, encodeURIComponent('Cash/Online'))
          .replace(/ContactNumber/g, encodeURIComponent(phone));

        // 4. Handle message= parameter if present
        if (url.includes('message=')) {
          const [baseUrl, queryString] = url.split('?');
          const params = new URLSearchParams(queryString);
          const messageVal = params.get('message') || '';

          if (messageVal.toLowerCase().includes('studentname') || messageVal.toLowerCase().includes('totalfee') || messageVal.toLowerCase().includes('fee,')) {
            const parts = messageVal.split(',');
            const replacedParts = parts.map((part) => {
              const trimmed = part.trim().toLowerCase();
              if (trimmed === 'studentname') return `Fee Report (${dateRangeStr})`;
              if (trimmed === 'class') return selectedClassStr;
              if (trimmed === 'division') return selectedDivStr;
              if (trimmed === 'duedate') return dateRangeStr || formattedDate;
              if (trimmed === 'totalfee') return grandTotalStr;
              if (trimmed === 'paidamount') return grandTotalStr;
              if (trimmed === 'pendingamount') return '0.00';
              if (trimmed === 'paymentmode') return 'Cash/Online';
              if (trimmed === 'schoolname') return schoolNameValue;
              if (trimmed === 'contactnumber') return phone;
              return part;
            });
            params.set('message', replacedParts.join(','));
            url = baseUrl + '?' + params.toString().replace(/%2C/g, ',').replace(/\+/g, '%20');
          } else if (!messageVal || messageVal === 'undefined') {
            params.set('message', encodeURIComponent(fullReportText));
            url = baseUrl + '?' + params.toString();
          }
        } else {
          url += `&message=${encodeURIComponent(fullReportText)}`;
        }

        console.log('[WhatsApp Report Webhook URL]:', url);
        return fetch(url, { method: 'GET', mode: 'no-cors' });
      });

      await Promise.all(sendPromises);
      alert("WhatsApp message sent successfully to Headmaster and Clerk!");

    } catch (error) {
      console.error("Error sending WhatsApp message:", error);
      alert("Failed to send WhatsApp message. Please try again.");
    } finally {
      setIsSendingWhatsApp(false);
    }
  };

  return (
    <div className='main-content-of-page'>
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button
          onClick={handleGoBack}
          style={{
            backgroundColor: '#FFFFFF',
            color: 'black',
            padding: '7px 20px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginRight: '20px',
            border: '1px solid black',
            marginTop: '20px',
            marginLeft: '30px',
          }}
        >
          <i className="fa fa-arrow-left" style={{ fontSize: '22px', marginRight: '8px' }}></i>
          Go Back
        </button>
        <button
          onClick={handlePrint}
          style={{
            backgroundColor: '#6495ED',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginLeft: '10px',
            marginTop: '20px'
          }}
        >
          Print Report
        </button>
        <button
          onClick={handleSendWhatsAppToHMAndClerk}
          disabled={isSendingWhatsApp}
          style={{
            backgroundColor: isSendingWhatsApp ? '#A0A0A0' : '#25D366',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '5px',
            cursor: isSendingWhatsApp ? 'not-allowed' : 'pointer',
            marginLeft: '15px',
            marginTop: '20px',
            display: 'flex',
            alignItems: 'center',
            fontWeight: 'bold',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
          }}
        >
          <FaWhatsapp style={{ fontSize: '20px', marginRight: '8px' }} />
          {isSendingWhatsApp ? 'Sending WhatsApp Report...' : 'Send WhatsApp Report (HM & Clerk)'}
        </button>
      </div>

      <div ref={printRef} className="table-container mb-5" style={{
        margin: '20px auto',
        border: '1px solid black',
        overflow: 'hidden',
        width: '1200px',
        padding: '10px'
      }}  >

        <style>
          {`
          table {
              width: 100%;
              border-collapse: collapse;
          }

          thead {
              background-color: #4CAF50;
              color: white;
          }

            @page {
            border: 1px solid black;
              size: auto;
              margin: 3mm;
              padding : 20px;
            }
          th, td {
              padding:10px 8px;
              text-align: center;
              border: 1px solid black;
          }
          tr:hover {
              background-color: #f1f1f1;
          }

          tfoot {
              font-weight: bold;
              background-color: #f9f9f9;
          }
          h5{
          margin-top:30px;}
        `}
        </style>

        <div className="receipt-header" style={{ border: '1px solid black', marginbottom: '10px', backgroundColor: '#F7F7F7', padding: '10px', fontSize: '14px' }} >

          <h8>{schoolDetails?.sansthaName || ' '}</h8>
          <h2 style={{ fontWeight: '600', marginBottom: '4px' }}>{schoolDetails?.schoolName || ' '}</h2>

          <div className='taludis' style={{ textAlign: 'center', marginBottom: '7px' }}>
            <h8>{language === "English" ? "Taluka" : " तालुका"}: {schoolDetails?.taluka},{language === "English" ? "District" : " जिल्हा"} : {schoolDetails?.jilha}</h8>
          </div>

          <h2 style={{
            display: 'block',
            border: '1px solid black',
            backgroundColor: 'black',
            color: 'white',
            padding: '2px',
            width: '180px',
            textAlign: 'center',
          }}>{language === "English" ? "Year Report" : " वर्षाचा अहवाल"}</h2>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginLeft: '1%', marginTop: '5px' }}>
            {selectedClass && selectedClass !== 'N/A' && selectedClass !== 'All' && selectedClass !== '' ? (
              <p style={{ margin: '0', fontWeight: 'bold' }}>
                Class: {selectedClass} {division && division !== 'N/A' && division !== 'All' && division.trim() !== '' ? ` (${language === "English" ? "Division" : "तुकडी"}: ${division})` : ''}
              </p>
            ) : <div />}
            {selectedYear && selectedYear !== 'N/A' && (
              <p style={{ margin: '0', fontWeight: 'bold' }}>
                Year: {selectedYear}
              </p>
            )}
          </div>
        </div>

        {Object.keys(invoiceGroups).length > 0 ? (
          Object.keys(invoiceGroups).sort().map(date => (
            <div key={date} style={{ marginTop: '16px' }}>
              <h5 style={{ marginBottom: '5px', marginLeft: '10px', fontSize: '16px' }}>{formatDate(date)}</h5>
              <table style={{ fontSize: '14px' }}>
                <thead>
                  <tr>
                    <th style={{ backgroundColor: '#6495ED' }} > {language === "English" ? "Sr. No" : "अनु.क्र."}</th>
                    <th style={{ backgroundColor: '#6495ED' }}>{language === "English" ? "Class" : "वर्ग"}</th>
                    <th style={{ backgroundColor: '#6495ED' }}>{language === "English" ? "Division" : "तुकडी"}</th>
                    <th style={{ backgroundColor: '#6495ED' }}>Invoice No</th>
                    <th style={{ backgroundColor: '#6495ED' }}>{language === "English" ? "Student Name" : "विद्यार्थ्याचे नाव"}</th>
                    <th style={{ backgroundColor: '#6495ED' }}>Reg. No</th>
                    <th style={{ backgroundColor: '#6495ED' }}>{language === "English" ? "Date" : "तारीख"}</th>
                    <th style={{ backgroundColor: '#6495ED' }}>{language === "English" ? "Transaction" : "व्यवहार"}</th>
                    <th style={{ backgroundColor: '#6495ED' }}>{language === "English" ? "Paid Fee" : "दिलेली फी"}</th>
                  </tr>
                </thead>

                <tbody>
                  {Object.values(invoiceGroups[date])
                    .filter((group) => {
                      const student = studentData[group.serialNo] || {};
                      const paidAmount = parseFloat(group.paidAmount || 0);
                      return paidAmount > 0 && student.isActive !== false;
                    })
                    .map((group, index) => {
                      const student = studentData[group.serialNo] || {};
                      const paidAmount = parseFloat(group.paidAmount || 0);
                      const stdFullName = student.stdName
                        ? `${student.stdName} ${student.stdFather || ''} ${student.stdSurname || ''}`.trim()
                        : (group.serialNo || '-');

                      return (
                        <tr key={group.invoiceNo}>
                          <td>{index + 1}</td>
                          <td>{group.class || student.currentClass || ' '}</td>
                          <td>{group.division || student.division || ' '}</td>
                          <td>{group.invoiceNo || '-'}</td>
                          <td>{stdFullName}</td>
                          <td>{student.registerNo || ' '}</td>
                          <td>{formatDate(group.pavatiDate)}</td>
                          <td>{group.transactionType || ' '}</td>
                          <td>{paidAmount.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                </tbody>

                <tfoot>
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'right' }}>{language === "English" ? "Total Paid Fee" : "एकूण दिलेली फी"} :</td>
                    <td>
                      {Object.values(invoiceGroups[date]).reduce((acc, group) => {
                        const student = studentData[group.serialNo] || {};
                        const paidAmount = parseFloat(group.paidAmount || 0);
                        // Only include paidAmount if the student is active and amount is positive
                        return student.isActive !== false && paidAmount > 0 ? acc + paidAmount : acc;
                      }, 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ))
        ) : (
          <p>{language === "English" ? "No fee details available." : "कोणतेही शुल्क तपशील उपलब्ध नाहीत."}</p>
        )}

        {/* GRAND TOTAL */}
        <h4 style={{ marginTop: "30px" }}>
          Grand Total :{" "}
          {Object.keys(invoiceGroups)
            .reduce((grand, date) => {
              return (
                grand +
                Object.values(invoiceGroups[date]).reduce((sum, inv) => {
                  const student = studentData[inv.serialNo] || {};
                  return student.isActive !== false
                    ? sum + inv.paidAmount
                    : sum;
                }, 0)
              );
            }, 0)
            .toFixed(2)}
        </h4>
      </div>
    </div>
  );
}

export default Yearpavati;

